/**
 * Caches the result of an asynchronous function to improve performance by avoiding redundant calls.
 * The cache can be stored either in memory or in a specified storage location (e.g., localStorage).
 *
 * @template T - The type of the asynchronous function to be cached.
 * @param {T} func - The asynchronous function whose result needs to be cached.
 * @param {string} cacheKey - A unique key to identify the cached result.
 * @param {Storage | undefined} cacheLocation - The storage location for the cache. If undefined, an in-memory cache is used.
 * @returns {(...funcArgs: Parameters<T>) => Promise<Awaited<ReturnType<T>>>} - A function that returns the cached result if available, otherwise calls the original function and caches its result.
 */
export default function cacheAsyncFunction<
  T extends (...args: any[]) => Promise<any>,
>(
  func: T,
  cacheKey: string,
  cacheLocation?: Storage | undefined,
): (...funcArgs: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
  const memoryCache = new Map<string, string>();

  return async (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
    const key = `${cacheKey}_${JSON.stringify(args)}`;
    const cachedResult = cacheLocation
      ? cacheLocation.getItem(key)
      : memoryCache.get(key);

    if (cachedResult) {
      return JSON.parse(cachedResult) as Awaited<ReturnType<T>>;
    }

    const result = await func(...args);
    if (cacheLocation) {
      cacheLocation.setItem(key, JSON.stringify(result));
    } else {
      memoryCache.set(key, JSON.stringify(result));
    }
    return result;
  };
}
